import { useMemo, useCallback } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { useAccount } from '../contexts/AccountContext';
import { 
  checkAccess,
  checkUIVisibility, 
  getTierLimits,
  FEATURE_ACCESS,
  ACCOUNT_STATES,
  tierMeetsMinimum,
} from '../config/accessConfig';
import { normalizePlanTier } from '../utils/planTier';

/**
 * useAccessControl - Unified access control hook
 * 
 * This is the SINGLE SOURCE OF TRUTH for all access control decisions in the UI.
 * Use this hook instead of manual isOwner(), hasPermission() checks.
 * 
 * FIVE dimensions checked in order:
 * 1. Account State (suspended/deleted = no access)
 * 2. User State (disabled/locked = no access)
 * 3. Role (owner, admin, manager, assistant, viewer)
 * 4. Tier (trial/free, solo/starter, team/pro, growth/business, enterprise)
 * 5. Scopes (fine-grained permissions)
 * 
 * @example
 * const { canAccess, canSee, limits, showUpgradeFor, isReadOnly } = useAccessControl();
 * 
 * // Check if user can access a feature
 * if (canAccess('cohosts.manage')) { ... }
 * 
 * // Check if account is in read-only mode (past_due, canceled)
 * if (isReadOnly) { showReadOnlyBanner(); }
 * 
 * // Check if UI element should be visible
 * if (canSee('toast.subscription')) { ... }
 * 
 * // Get tier limits
 * const { teamSize, vehicles } = limits;
 * 
 * // Check if upgrade prompt should show
 * if (showUpgradeFor('reports.export.excel')) { ... }
 */
export function useAccessControl() {
  const permissions = usePermissions();
  const accountContext = useAccount();
  
  // Build user context from permissions and account
  const userContext = useMemo(() => {
    const activeAccount = accountContext?.activeAccount;
    
    // v5: planTier is now the RESOLVED tier from backend
    // "trial" = 30-day full access, "free" = post-trial limited, or paid tier
    const tier = normalizePlanTier(activeAccount?.planTier) || 'free';
    
    // Determine account state for Gate #1
    let accountState = 'active';
    if (activeAccount?.subscriptionStatus) {
      const status = activeAccount.subscriptionStatus;
      if (status === 'suspended') accountState = 'suspended';
      else if (status === 'deleted') accountState = 'deleted';
      else if (status === 'canceled') accountState = 'canceled';
      else if (status === 'past_due') accountState = 'past_due';
      else if (status === 'active') accountState = 'active';
      else if (status === 'cancellation_pending') accountState = 'active';
      else if (status === 'expired') accountState = 'canceled';
      // Free: 'trial'/'free' status → treat as 'free' (always has access)
      else if (status === 'trial' || status === 'trialing' || status === 'free') accountState = 'free';
    }
    
    // Determine user state for Gate #2 from user context
    // Default to 'active' for authenticated users without explicit state
    // (prevents navigation from being blocked when account context is loading)
    const userState = permissions?.user?.userState || permissions?.user?.status || activeAccount?.userState || 'active';
    
    return {
      role: permissions?.role || 'viewer',
      tier,
      scopes: permissions?.scopes || [],
      isOwner: permissions?.isOwner?.() ?? false,
      accountId: activeAccount?.accountId,
      accountState,
      userState,
    };
  }, [permissions, accountContext]);
  
  // Check if account is in read-only mode
  const isReadOnly = useMemo(() => {
    const stateConfig = ACCOUNT_STATES[userContext.accountState];
    return stateConfig?.readOnly ?? false;
  }, [userContext.accountState]);
  
  // Check if account is blocked entirely
  const isBlocked = useMemo(() => {
    const stateConfig = ACCOUNT_STATES[userContext.accountState];
    return !(stateConfig?.allowed ?? true);
  }, [userContext.accountState]);

  /**
   * Check if user can access a specific feature
   * @param {string} feature - Feature key from accessConfig
   * @returns {boolean}
   */
  const canAccess = useCallback((feature) => {
    const result = checkAccess(feature, userContext);
    return result.allowed;
  }, [userContext]);

  /**
   * Check if user can access a feature, with detailed result
   * @param {string} feature - Feature key from accessConfig
   * @returns {{ allowed: boolean, reason?: string, requiredTier?: string }}
   */
  const checkFeatureAccess = useCallback((feature) => {
    return checkAccess(feature, userContext);
  }, [userContext]);

  /**
   * Check if a UI element should be visible for current user
   * @param {string} element - UI element key from accessConfig
   * @returns {boolean}
   */
  const canSee = useCallback((element) => {
    return checkUIVisibility(element, userContext.role);
  }, [userContext.role]);

  /**
   * Check if upgrade prompt should be shown for a feature
   * (User has role access but not tier access)
   * @param {string} feature - Feature key
   * @returns {boolean}
   */
  const showUpgradeFor = useCallback((feature) => {
    const config = FEATURE_ACCESS[feature];
    if (!config) return false;
    
    // Check if user has role access
    const hasRoleAccess = config.roles.includes(userContext.role);
    if (!hasRoleAccess) return false;
    
    // Check if user lacks tier access
    const hasTierAccess = tierMeetsMinimum(userContext.tier, config.minTier);
    return !hasTierAccess;
  }, [userContext]);

  /**
   * Get current tier limits
   */
  const limits = useMemo(() => {
    return getTierLimits(userContext.tier);
  }, [userContext.tier]);

  /**
   * Check if user is within team size limit
   * @param {number} currentTeamSize 
   * @returns {boolean}
   */
  const canAddTeamMember = useCallback((currentTeamSize) => {
    return currentTeamSize < limits.teamSize;
  }, [limits.teamSize]);

  /**
   * Check if user is within vehicle limit
   * @param {number} currentVehicleCount 
   * @returns {boolean}
   */
  const canAddVehicle = useCallback((currentVehicleCount) => {
    return currentVehicleCount < limits.vehicles;
  }, [limits.vehicles]);

  /**
   * Get minimum tier required for a feature
   * @param {string} feature 
   * @returns {string|null}
   */
  const getRequiredTier = useCallback((feature) => {
    const config = FEATURE_ACCESS[feature];
    return config?.minTier || null;
  }, []);

  /**
   * Check if current user is an owner
   */
  const isOwner = userContext.isOwner;

  /**
   * Check if current user is an admin or owner
   */
  const isAdminOrOwner = useMemo(() => {
    return ['owner', 'admin'].includes(userContext.role);
  }, [userContext.role]);

  /**
   * Check if current user can manage (manager or above)
   */
  const canManage = useMemo(() => {
    return ['owner', 'admin', 'manager'].includes(userContext.role);
  }, [userContext.role]);

  /**
   * Check if current user can edit data (assistant or above)
   */
  const canEdit = useMemo(() => {
    return ['owner', 'admin', 'manager', 'assistant'].includes(userContext.role);
  }, [userContext.role]);

  /**
   * Current role
   */
  const role = userContext.role;

  /**
   * Current tier
   */
  const tier = userContext.tier;

  // Loading state from permissions context
  const permissionsLoading = permissions?.loading ?? false;

  return {
    // Core access checks
    canAccess,
    checkFeatureAccess,
    canSee,
    showUpgradeFor,
    
    // Account state checks
    isReadOnly,    // Account in read-only mode (past_due, canceled)
    isBlocked,     // Account blocked entirely (suspended, deleted)
    accountState: userContext.accountState,
    
    // Loading state - true while permissions are being loaded
    loading: permissionsLoading,
    
    // Tier limits
    limits,
    canAddTeamMember,
    canAddVehicle,
    getRequiredTier,
    
    // Quick role checks
    isOwner,
    isAdminOrOwner,
    canManage,
    canEdit,
    
    // Context
    role,
    tier,
    userContext,
  };
}

/**
 * AccessGate component - Conditionally render children based on access
 * Waits for permissions to load before showing fallback to prevent flash
 * 
 * @example
 * <AccessGate feature="cohosts.manage">
 *   <CohostManager />
 * </AccessGate>
 * 
 * <AccessGate feature="reports.export.excel" fallback={<UpgradePrompt />}>
 *   <ExportButton />
 * </AccessGate>
 */
export function AccessGate({ feature, children, fallback = null }) {
  const { canAccess, loading } = useAccessControl();
  
  // While loading, show children to prevent flash of fallback
  if (loading) {
    return children;
  }
  
  if (canAccess(feature)) {
    return children;
  }
  
  return fallback;
}

/**
 * OwnerGate component - Only render for account owners
 * Waits for permissions to load before showing fallback
 */
export function OwnerGate({ children, fallback = null }) {
  const { isOwner, loading } = useAccessControl();
  
  // While loading, show children to prevent flash of fallback
  if (loading) {
    return children;
  }
  
  if (isOwner) {
    return children;
  }
  
  return fallback;
}

/**
 * TierGate component - Only render if user meets minimum tier
 * Waits for permissions to load before showing fallback
 */
export function TierGate({ minTier, children, fallback = null }) {
  const { tier, loading } = useAccessControl();
  
  // While loading, show children to prevent flash of fallback
  if (loading) {
    return children;
  }
  
  if (tierMeetsMinimum(tier, minTier)) {
    return children;
  }
  
  return fallback;
}

export default useAccessControl;
